/**
 * IframeLayout — the app shell, in its two settings. Both carry the welcome
 * tour (`tour/Tour.tsx`), which starts on a person's first visit to Home.
 *
 * - Embedded in a Rumio site: the host supplies the chrome, so this renders
 *   only a row of section links above the screen, takes the host's theme, and
 *   keeps the host informed of height and location.
 * - On its own (a standalone account at the app's address): a sidebar on wide
 *   screens, a top bar with a slide-out menu on narrow ones.
 *
 * Either way the screen itself sits inside an error boundary, so a screen that
 * fails leaves the navigation working.
 */

import { Suspense, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { Menu, X } from "lucide-react";
import {
  inIframe,
  startHeightSync,
  sendLocation,
  listenForNavigation,
} from "@/lib/iframe-bridge";
import { helpWidgetKey, loadHelpWidget } from "@/lib/help";
import { AccountMenu, useBilling } from "@/lib/billing";
import { useRumioSession } from "@/lib/session";
import { applyTheme, listenForDesign } from "@/lib/theme";
import { useVisibleArea } from "@/lib/visible-area";
import { NAV } from "@/routes";
import { ErrorBoundary } from "./ErrorBoundary";
import { TourProvider } from "./tour/Tour";
import { Card, Spinner, cn } from "./ui";

const focusRing = "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function IframeLayout() {
  const isEmbedded = inIframe();
  const location = useLocation();
  const navigate = useNavigate();
  const { claims, token } = useRumioSession();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!isEmbedded) return;
    return startHeightSync();
  }, [isEmbedded]);

  useEffect(() => {
    if (!isEmbedded) return;
    sendLocation(location.pathname + location.search);
  }, [isEmbedded, location.pathname, location.search]);

  useEffect(() => {
    if (!isEmbedded) return;
    return listenForNavigation((path) => {
      if (path !== location.pathname + location.search) navigate(path);
    });
  }, [isEmbedded, location, navigate]);

  // The host's theme: a snapshot in the session token, then live updates by
  // message. Once a live one has arrived it is the newer of the two, so a
  // later token refresh doesn't put the snapshot back over it.
  const liveTheme = useRef(false);
  useEffect(() => {
    const theme = claims?.theme;
    if (theme && !liveTheme.current) applyTheme(theme);
    // `claims` is decoded afresh on every render; the token is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!isEmbedded) return;
    return listenForDesign((tokens) => {
      liveTheme.current = true;
      applyTheme(tokens);
    });
  }, [isEmbedded]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // On its own the app shows the help center's bubble; inside a site, the
  // site's own widget is already around the frame.
  useEffect(() => {
    if (isEmbedded) return;
    void helpWidgetKey().then((key) => {
      if (key) void loadHelpWidget(key);
    });
  }, [isEmbedded]);

  // Toasts are `fixed` at the top of the frame; embedded, that is wherever the host page
  // has scrolled the frame's top edge to. The toaster is mounted once in main.tsx, so its
  // offset is moved here, to the top of the visible part (sonner reads --offset-top).
  const visible = useVisibleArea();
  useEffect(() => {
    if (!isEmbedded || !visible) return;
    const el = document.querySelector<HTMLElement>("[data-sonner-toaster]");
    el?.style.setProperty("--offset-top", `${Math.round(visible.top + 16)}px`);
  }, [isEmbedded, visible]);

  const signedOut = !claims?.userId;
  const screen = signedOut ? (
    <SignedOutNotice tenant={claims?.tenant} />
  ) : (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<Spinner />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );

  if (isEmbedded) {
    return (
      <TourProvider enabled={!signedOut}>
      <div className="bg-background text-foreground">
        <main className="p-4 md:p-6">
          {/* The parent supplies the chrome but not this app's sections. */}
          {!signedOut && (
            <nav aria-label="Sections" className="mx-auto mb-4 flex max-w-4xl gap-1 overflow-x-auto">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition",
                      focusRing,
                      isActive ? "bg-primary font-medium text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )
                  }
                >
                  <item.icon size={14} aria-hidden="true" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}
          {screen}
        </main>
      </div>
      </TourProvider>
    );
  }

  return (
    <TourProvider enabled={!signedOut}>
    <div className="min-h-screen bg-background text-foreground">
      <aside className="hidden border-r border-border bg-card md:fixed md:inset-y-0 md:flex md:w-60 md:flex-col">
        <Sidebar />
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card px-4 py-3 md:hidden">
        <Wordmark />
        <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <Dialog.Trigger
            aria-label="Open menu"
            className={cn("-mr-2 rounded-md p-2 text-foreground hover:bg-muted", focusRing)}
          >
            <Menu size={20} aria-hidden="true" />
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 md:hidden" />
            <Dialog.Content
              aria-describedby={undefined}
              className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-border bg-card text-card-foreground shadow-xl focus:outline-none md:hidden"
            >
              <Dialog.Title className="sr-only">Menu</Dialog.Title>
              <Dialog.Close
                aria-label="Close menu"
                className={cn("absolute right-2 top-2.5 rounded-md p-2 text-muted-foreground hover:bg-muted", focusRing)}
              >
                <X size={18} aria-hidden="true" />
              </Dialog.Close>
              <Sidebar />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </header>

      <main className="p-4 sm:p-6 md:ml-60 md:p-8">{screen}</main>
    </div>
    </TourProvider>
  );
}

function Wordmark() {
  return (
    <span className="flex items-center gap-2 text-base font-semibold tracking-tight">
      <span aria-hidden="true" className="grid h-6 w-6 place-items-center rounded-md bg-primary text-xs font-bold text-primary-foreground">
        L
      </span>
      Lettrova
    </span>
  );
}

/** The standalone navigation: the same content in the desktop sidebar and the phone menu. */
function Sidebar() {
  return (
    <>
      <div className="border-b border-border px-5 py-4">
        <Wordmark />
      </div>
      <nav aria-label="Sections" className="flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition",
                focusRing,
                isActive ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )
            }
          >
            <item.icon size={16} aria-hidden="true" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <Account />
    </>
  );
}

/**
 * Who is signed in, and the way out. Only a standalone account has either:
 * a Rumio user's session belongs to the host site, and the app is never told
 * their email address.
 */
function Account() {
  const { standalone } = useRumioSession();
  if (!standalone) return null;
  return <StandaloneAccount />;
}

function StandaloneAccount() {
  const { billing } = useBilling();
  return (
    <div className="border-t border-border px-5 py-3">
      <AccountMenu email={billing?.email} className="flex flex-col items-stretch gap-2 text-sm" />
    </div>
  );
}

/** Shown when there is no signed-in Rumio user (stores belong to a user). */
function SignedOutNotice({ tenant }: { tenant?: string }) {
  return (
    <Card flush role="status" className="mx-auto max-w-md p-6 text-center">
      <h1 className="text-base font-semibold">Sign in to use Lettrova</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Your connected stores are tied to your account. Sign in
        {tenant ? <> on <span className="font-medium text-foreground">{tenant}</span></> : null} and reopen this page.
      </p>
    </Card>
  );
}
